import { NavLink, Routes, Route, Link } from "react-router-dom";
import { LayoutDashboard, Package, ShoppingCart, Truck, RotateCcw, Wallet, Bell, Settings, Shield, Users, MessageSquare, Store } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";
import { Logo, LangSwitch } from "@/components/store/Header";
import Dashboard from "@/pages/admin/Dashboard";
import { ProductsList, ProductEdit } from "@/pages/admin/Products";
import { OrdersList, OrderDetail, SupplierOrders } from "@/pages/admin/Orders";
import { ReturnsList, ReturnDetail } from "@/pages/admin/Returns";
import Finance from "@/pages/admin/Finance";
import { Alerts, SettingsPage, SecurityPage, Staff, SupportInbox } from "@/pages/admin/System";

const NAV = [
  ["", "dashboard", LayoutDashboard, "dashboard.view"], ["products", "products", Package, "products.manage"], ["orders", "orders", ShoppingCart, "orders.view"],
  ["supplier", "supplier", Truck, "orders.view"], ["returns", "returns", RotateCcw, "returns.manage"], ["finance", "finance", Wallet, "wallet.view"],
  ["alerts", "alerts", Bell, "dashboard.view"], ["support", "support", MessageSquare, "support.view"], ["settings", "settings", Settings, "settings.view"],
  ["security", "security", Shield, "dashboard.view"], ["staff", "staff", Users, "staff.manage"],
];

export const PageHead = ({ title, sub, children }) => (
  <div className="flex flex-wrap items-end justify-between gap-3 mb-6"><div><h1 className="text-2xl sm:text-3xl font-bold tracking-tight">{title}</h1>{sub && <p className="text-sm muted mt-1">{sub}</p>}</div>{children}</div>
);

export const Table = ({ cols, rows, empty, testid }) => {
  const { t } = useI18n();
  return (
    <div className="card overflow-x-auto" data-testid={testid}>
      <table className="w-full"><thead className="bg-[#F9FAFB] border-b border-[#E5E7EB]"><tr>{cols.map((c) => <th key={c[0]} className="th">{c[0]}</th>)}</tr></thead>
        <tbody className="divide-y divide-[#F3F4F6]">{rows.length === 0 ? <tr><td className="td muted text-center py-10" colSpan={cols.length}>{empty || t("common.empty")}</td></tr> :
          rows.map((r, i) => <tr key={i} className="hover:bg-[#F9FAFB]">{cols.map((c) => <td key={c[0]} className="td">{c[1](r)}</td>)}</tr>)}</tbody></table>
    </div>
  );
};

export default function AdminApp() {
  const { t } = useI18n();
  const { user, can } = useAuth();
  const items = NAV.filter((n) => can(n[3]));
  return (
    <div className="min-h-screen bg-[#F5F7FA]" data-testid="admin-app">
      <aside className="hidden lg:flex fixed inset-y-0 start-0 w-64 bg-[#0B0D0F] text-white flex-col">
        <div className="h-16 px-6 flex items-center gap-3 border-b border-white/10"><Logo light /><span className="chip bg-white/10 text-white/70">{t("admin.panel")}</span></div>
        <nav className="flex-1 overflow-y-auto p-3 space-y-1">{items.map(([to, k, I]) => (
          <NavLink key={k} to={`/admin/${to}`} end={to === ""} data-testid={`admin-nav-${k}`} className={({ isActive }) => `flex items-center gap-3 px-3 h-11 rounded-xl text-sm transition-colors ${isActive ? "bg-[#2563EB] text-white" : "text-white/70 hover:bg-white/5 hover:text-white"}`}><I className="w-4 h-4" />{t(`admin.nav_${k}`)}</NavLink>))}</nav>
        <div className="p-4 border-t border-white/10 space-y-3 text-xs text-white/60">
          <p>{user.email}<br /><span className="text-white/40">{t(`roles.${user.role}`)}</span></p>
          <div className="flex justify-between"><LangSwitch className="text-white/70" /><Link to="/" className="flex items-center gap-1 hover:text-white"><Store className="w-4 h-4" />{t("admin.store")}</Link></div>
        </div>
      </aside>
      <header className="lg:hidden sticky top-0 z-30 bg-[#0B0D0F] text-white">
        <div className="h-14 px-4 flex items-center justify-between"><Logo light /><div className="flex gap-3"><LangSwitch className="text-white/80" /><Link to="/" className="text-white/80"><Store className="w-5 h-5" /></Link></div></div>
        <nav className="flex gap-1 overflow-x-auto scrollbar-none px-3 pb-3">{items.map(([to, k, I]) => (
          <NavLink key={k} to={`/admin/${to}`} end={to === ""} className={({ isActive }) => `flex items-center gap-1.5 px-3 h-9 rounded-full text-xs shrink-0 ${isActive ? "bg-[#2563EB]" : "bg-white/10"}`}><I className="w-3.5 h-3.5" />{t(`admin.nav_${k}`)}</NavLink>))}</nav>
      </header>
      <main className="lg:ps-64"><div className="p-4 sm:p-8 max-w-7xl">
        <Routes>
          <Route index element={<Dashboard />} />
          <Route path="products" element={<ProductsList />} />
          <Route path="products/:id" element={<ProductEdit />} />
          <Route path="orders" element={<OrdersList />} />
          <Route path="orders/:id" element={<OrderDetail />} />
          <Route path="supplier" element={<SupplierOrders />} />
          <Route path="returns" element={<ReturnsList />} />
          <Route path="returns/:id" element={<ReturnDetail />} />
          <Route path="finance" element={<Finance />} />
          <Route path="alerts" element={<Alerts />} />
          <Route path="support" element={<SupportInbox />} />
          <Route path="settings" element={<SettingsPage />} />
          <Route path="security" element={<SecurityPage />} />
          <Route path="staff" element={<Staff />} />
        </Routes>
      </div></main>
    </div>
  );
}
